import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Search, FileText, Settings, Users, Rocket, Headphones } from 'lucide-react';

const ProcessSection = () => {
  const steps = [
    {
      icon: <Search className="h-8 w-8 text-blue-600" />,
      title: "Kennenlernen & Bedarfsanalyse",
      duration: "1 Woche",
      description: "Wir schauen uns dein Unternehmen an: Wie arbeitest du heute? Was läuft gut, was nervt? Welche Ziele hast du?",
      details: [
        "Unverbindliches Erstgespräch",
        "Deine aktuellen Prozesse verstehen",
        "Gemeinsam Ziele definieren",
        "Kostenloses Angebot erstellen"
      ]
    },
    {
      icon: <FileText className="h-8 w-8 text-green-600" />,
      title: "Planung & Vorbereitung",
      duration: "1-2 Wochen",
      description: "Wir planen die Einführung: Welche Funktionen brauchst du zuerst? Wie soll dein System aussehen?",
      details: [
        "Funktionen festlegen",
        "Benutzer und Rechte planen",
        "Zeitplan erstellen",
        "Test-System einrichten"
      ]
    },
    {
      icon: <Settings className="h-8 w-8 text-purple-600" />,
      title: "System einrichten",
      duration: "2-4 Wochen",
      description: "Wir richten Business Central für dich ein: Deine Artikel, Kunden, Preise - alles wird sauber angelegt.",
      details: [
        "Grundeinstellungen konfigurieren",
        "Artikel und Preise anlegen",
        "Kunden und Lieferanten erfassen",
        "Berichte und Vorlagen anpassen"
      ]
    },
    {
      icon: <Users className="h-8 w-8 text-orange-600" />,
      title: "Team schulen",
      duration: "1 Woche",
      description: "Dein Team lernt das neue System kennen: Praktische Schulungen mit echten Beispielen aus deinem Alltag.",
      details: [
        "Schulungen für alle Nutzer",
        "Praktische Übungen",
        "Handbuch für dein Unternehmen",
        "Fragen klären"
      ]
    },
    {
      icon: <Rocket className="h-8 w-8 text-red-600" />,
      title: "Live-Start",
      duration: "1 Woche",
      description: "Jetzt geht's los! Wir begleiten dich beim Start und sind da, wenn Fragen aufkommen.",
      details: [
        "Gemeinsamer Start",
        "Erste Rechnungen schreiben",
        "Bestellungen erfassen",
        "Sofort-Support bei Problemen"
      ]
    },
    {
      icon: <Headphones className="h-8 w-8 text-teal-600" />,
      title: "Betreuung & Optimierung",
      duration: "Fortlaufend",
      description: "Auch nach dem Start sind wir für dich da: Support, Updates und neue Funktionen nach Bedarf.",
      details: [
        "Telefon-Support",
        "Regelmäßige Check-ups",
        "Neue Funktionen einführen",
        "System weiterentwickeln"
      ]
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Dein Weg zu Microsoft Business Central
          </h2>
          <p className="text-lg text-gray-600">
            So läuft die Einführung von Business Central in einem kleinen Unternehmen ab - 
            Schritt für Schritt, ohne Stress und mit persönlicher Betreuung.
          </p>
        </div>

        {/* Beispiel-Unternehmen */}
        <div className="bg-blue-50 rounded-xl p-8 mb-16 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-center mb-6 text-blue-900">
            Beispiel: Metallbau Schmidt GmbH
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">12</div>
              <div className="text-sm text-blue-800">Mitarbeiter</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600 mb-2">2,5 Mio €</div>
              <div className="text-sm text-green-800">Jahresumsatz</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 mb-2">6 Wochen</div>
              <div className="text-sm text-purple-800">Bis zum Live-Start</div>
            </div>
          </div>
          <p className="text-center mt-6 text-blue-800">
            <strong>Situation:</strong> Bisher alles mit Excel und separater Buchhaltungssoftware. 
            Wollten endlich alles in einem System haben.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <Card key={index} className="border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all duration-300 relative">
              <CardContent className="p-6">
                {/* Schritt-Nummer */}
                <div className="absolute -top-4 -left-4 bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                  {index + 1}
                </div>
                
                {/* Icon */}
                <div className="mb-4">
                  {step.icon}
                </div>
                
                {/* Titel und Dauer */}
                <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                <div className="text-sm text-blue-600 font-semibold mb-3">
                  Dauer: {step.duration}
                </div>
                
                {/* Beschreibung */}
                <p className="text-gray-600 mb-4">
                  {step.description}
                </p>
                
                {/* Details */}
                <ul className="space-y-1">
                  {step.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="text-sm text-gray-500 flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      {detail}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Zeitlinie */}
        <div className="mt-16 bg-green-50 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-center mb-8 text-green-900">
            Typische Projektdauer: 6-8 Wochen
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">1-3</div>
              <div className="text-sm text-gray-600">Wochen Planung</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600 mb-2">2-4</div>
              <div className="text-sm text-gray-600">Wochen Einrichtung</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 mb-2">1-2</div>
              <div className="text-sm text-gray-600">Wochen Schulung & Start</div>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <div className="bg-white rounded-lg p-6 max-w-2xl mx-auto">
              <h4 className="font-bold text-gray-900 mb-3">Das Ergebnis bei Metallbau Schmidt:</h4>
              <ul className="text-left space-y-2 text-gray-600">
                <li>• Angebote werden 50% schneller erstellt</li>
                <li>• Lagerbestände sind immer aktuell</li>
                <li>• Rechnungen gehen automatisch raus</li>
                <li>• Monatsabschluss dauert nur noch 2 Tage statt 2 Wochen</li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-green-800 mb-4">
              <strong>Wichtig:</strong> Wir arbeiten transparent und in kleinen Schritten. 
              Du siehst jederzeit den Fortschritt und kannst Feedback geben.
            </p>
            <a 
              href="#contact" 
              className="inline-block bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Dein Projekt besprechen
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection; 